import { useState, type ReactNode } from 'react';
import {
  BellRing,
  CalendarDays,
  ClipboardCheck,
  FileCheck2,
  FileText,
  GraduationCap,
  Layers,
  MessageSquare,
  MonitorPlay,
  PenLine,
  TriangleAlert,
  UserPlus,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { Chargement, MessageErreur, Pastille } from '../components/ui/Primitives';
import { VisionneusePiece } from '../components/dossier/VisionneusePiece';
import {
  lienAccesSession,
  urlConvocationApprenant,
  useFichesATraiter,
  useFinFormation,
  useTableauDeBordFormateur,
  type ApprenantDuFormateur,
  type DateCleLue,
  type PromotionDuFormateur,
  type SessionDuJour,
  type TableauDeBordFormateur,
  type TravailACorriger,
} from '../lib/useEspaceFormateur';
import { useMaFicheFormateur } from '../lib/useFormateurs';
import { useNotifications } from '../lib/useNotifications';
import { momentRelatif } from '../lib/moment-relatif';
import { jourLisible } from '../components/ecf/format';
import { BarreAvancement } from '../components/lecteur/Progression';

/**
 * Tableau de bord du formateur (CDC §7, §7.1 ; TF-391, TF-456). Tout ce qui
 * s'y lit vient de l'API : ses sessions du jour et leur accès direct, sa file
 * de corrections, ses promotions — leur taux moyen et leurs apprenants en
 * retard, détaillés dans « Mes promotions » (TF-458) —, ses apprenants — ceux
 * dont il est le référent —, leurs dates et leurs convocations, ses fiches
 * séquence en attente, ses pièces selon son profil salarié ou indépendant, et
 * ses notifications.
 */

const LIEN_DISCRET =
  'inline-flex items-center text-sm font-medium text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[40px]';

function pluriel(nombre: number, singulier: string, plurielForme = `${singulier}s`): string {
  return `${nombre} ${nombre > 1 ? plurielForme : singulier}`;
}

function Indicateur({
  icone: IconeIndicateur,
  teinte,
  libelle,
  valeur,
  detail,
}: {
  icone: LucideIcon;
  teinte: string;
  libelle: string;
  valeur: number;
  detail: string;
}): JSX.Element {
  return (
    <div className="stat-card rounded-2xl bg-white p-4 shadow-card sm:p-5">
      <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-lg ${teinte}`}>
        <IconeIndicateur className="h-5 w-5" aria-hidden="true" />
      </div>
      <div className="text-[11px] font-medium uppercase tracking-wide text-codialis-gray sm:text-xs">{libelle}</div>
      <div className="font-display mt-1 text-3xl font-extrabold text-codialis-navy">{valeur}</div>
      <div className="mt-1 text-xs text-codialis-gray">{detail}</div>
    </div>
  );
}

function CarteSection({
  titre,
  sousTitre,
  lien,
  children,
  bordure,
}: {
  titre: string;
  sousTitre?: string;
  lien?: { to: string; libelle: string } | null;
  children: ReactNode;
  bordure?: string;
}): JSX.Element {
  return (
    <section className={`min-w-0 rounded-2xl bg-white p-4 shadow-card sm:p-6 ${bordure ?? ''}`}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="font-display text-lg font-bold text-codialis-navy">{titre}</h2>
          {sousTitre && <p className="text-sm text-codialis-gray">{sousTitre}</p>}
        </div>
        {lien && (
          <Link to={lien.to} className={LIEN_DISCRET}>
            {lien.libelle} →
          </Link>
        )}
      </div>
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sessions du jour
// ---------------------------------------------------------------------------

const DEMI_JOURNEE: Record<SessionDuJour['demiJournee'], string> = { MATIN: 'Matin', APRES_MIDI: 'Après-midi' };

const LIBELLES_ACCES: Record<SessionDuJour['avancement'], string> = {
  A_OUVRIR: 'Ouvrir l’émargement',
  EN_COURS: 'Reprendre l’émargement',
  TERMINEE: 'Voir la feuille',
};

/** Ce que fait le bouton d'une ligne, et l'écran qu'il ouvre. */
function actionSession(ligne: SessionDuJour): { ecran: string; libelle: string; lien: string } | null {
  if (!ligne.acces || ligne.refusAcces) return null;
  const ecran = ligne.acces.type === 'SALLE' ? 'form-elearning' : 'emargement';
  const libelle =
    ligne.acces.type === 'SALLE'
      ? 'Rejoindre la salle'
      : ligne.acces.type === 'CREATION'
        ? 'Créer la feuille'
        : LIBELLES_ACCES[ligne.avancement];
  return { ecran, libelle, lien: lienAccesSession(ligne.acces) };
}

function LigneSessionDuJour({ ligne, prochaine }: { ligne: SessionDuJour; prochaine: boolean }): JSX.Element {
  const navigate = useNavigate();
  const { peutAcceder } = useAuth();
  const aDistance = ligne.modalite === 'ELEARNING';
  const IconeLigne = aDistance ? MonitorPlay : Users;
  const action = actionSession(ligne);
  return (
    <div
      className={`flex flex-col gap-3 rounded-xl p-4 sm:flex-row sm:items-start ${
        prochaine ? 'border-2 border-amber-200 bg-amber-50/50' : 'border border-gray-100'
      }`}
    >
      <div
        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-white ${
          aDistance ? 'bg-codialis-sky' : 'gradient-warm'
        }`}
      >
        <IconeLigne className="h-6 w-6" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <Pastille texte={ligne.etatLibelle} ton={ligne.etatTon} />
          <span className="text-xs text-codialis-gray">
            {DEMI_JOURNEE[ligne.demiJournee]} · {ligne.creneau} ·{' '}
            {aDistance ? 'À distance' : (ligne.lieu ?? 'Salle à préciser')}
          </span>
        </div>
        <div className="font-display font-bold text-codialis-navy">{ligne.libelle}</div>
        <div className="text-sm text-codialis-gray">
          {[ligne.promotion, `Antenne ${ligne.siteCode}`, pluriel(ligne.attendus, 'apprenant attendu', 'apprenants attendus')]
            .filter(Boolean)
            .join(' · ')}
        </div>
        {ligne.refusAcces && <p className="mt-1 text-xs text-amber-700">{ligne.refusAcces}</p>}
      </div>
      {action && peutAcceder(action.ecran) && (
        <button
          type="button"
          onClick={() => navigate(action.lien)}
          className="shrink-0 rounded-lg bg-codialis-navy px-3 py-2 text-xs font-semibold text-white transition hover:bg-codialis-deep [@media(pointer:coarse)]:min-h-[40px]"
        >
          {action.libelle}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// File de corrections
// ---------------------------------------------------------------------------

function initiales(nom: string): string {
  return nom
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((mot) => mot.charAt(0).toUpperCase())
    .join('');
}

/** Chaque ligne ouvre sa correction dans « Corrections » (TF-457). */
function LigneCorrection({ ligne }: { ligne: TravailACorriger }): JSX.Element {
  const note = ligne.noteAutomatique;
  return (
    <li>
      <Link
        to={`/instructor-grading?travail=${encodeURIComponent(ligne.cle)}`}
        className="flex items-start gap-3 rounded-xl border border-gray-100 p-3 transition hover:border-codialis-mint/40 hover:bg-gray-50 sm:items-center"
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-codialis-navy text-xs font-bold text-white">
          {initiales(ligne.apprenant)}
        </div>
        {/* Sur téléphone, les pastilles passent sous le texte plutôt que de le rogner. */}
        <div className="min-w-0 flex-1 sm:flex sm:items-center sm:gap-3">
          <div className="min-w-0 sm:flex-1">
            <div className="text-sm font-semibold text-codialis-navy sm:truncate">
              {ligne.titre}
              <span className="font-normal text-codialis-gray"> · {ligne.typeLibelle}</span>
            </div>
            <div className="text-xs text-codialis-gray sm:truncate">
              {[ligne.apprenant, ligne.promotion, ligne.contexte].join(' · ')}
            </div>
            <div className="text-[11px] text-codialis-gray">
              Rendu {momentRelatif(ligne.renduLe)}
              {ligne.depot !== null && ligne.depot > 1 && ` · dépôt n°${ligne.depot}`}
            </div>
          </div>
          <div className="mt-1.5 flex shrink-0 flex-wrap gap-1 sm:mt-0 sm:flex-col sm:items-end">
            {note && <Pastille texte={`Note auto ${String(note.sur20).replace('.', ',')}/20`} ton="mint" />}
            {note && <Pastille texte="À valider" ton="info" />}
            {ligne.enRetard ? (
              <Pastille texte="Rendu en retard" ton="danger" />
            ) : (
              !note && <Pastille texte="À corriger" ton="warning" />
            )}
            {ligne.brouillon && <Pastille texte="Brouillon" ton="neutral" />}
          </div>
        </div>
      </Link>
    </li>
  );
}

function CarteCorrections({ corrections }: { corrections: TableauDeBordFormateur['corrections'] }): JSX.Element {
  const { devoirs, quiz } = corrections.compteurs;
  const total = devoirs + quiz;
  const autres = total - corrections.lignes.length;
  return (
    <CarteSection
      titre="File de corrections"
      lien={{ to: '/instructor-grading', libelle: total === 0 ? 'Mes corrections' : 'Tout corriger' }}
      sousTitre={
        total === 0
          ? 'Aucun travail en attente de correction'
          : `${pluriel(devoirs, 'devoir')} · ${pluriel(quiz, 'quiz', 'quiz')} en attente de correction`
      }
    >
      {total === 0 ? (
        <p className="text-sm text-codialis-gray">
          Un devoir rendu par l’un de vos apprenants, ou un quiz terminé, s’ajoute ici jusqu’à la publication de sa
          correction.
        </p>
      ) : (
        <>
          <ul className="space-y-2.5">
            {corrections.lignes.map((ligne) => (
              <LigneCorrection key={ligne.cle} ligne={ligne} />
            ))}
          </ul>
          {autres > 0 && (
            <p className="mt-3 text-xs text-codialis-gray">
              Et {pluriel(autres, 'autre travail', 'autres travaux')}, rendu{autres > 1 ? 's' : ''} plus récemment.
            </p>
          )}
        </>
      )}
    </CarteSection>
  );
}

// ---------------------------------------------------------------------------
// Mes promotions
// ---------------------------------------------------------------------------

function CartePromotion({ promotion }: { promotion: PromotionDuFormateur }): JSX.Element {
  const { peutAcceder } = useAuth();
  const contenu = (
    <>
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
          <Layers className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold leading-tight text-codialis-navy">{promotion.libelle}</div>
          <div className="text-xs text-codialis-gray">
            {[promotion.libelle === promotion.formation ? null : promotion.formation, `Antenne ${promotion.siteCode}`, promotion.mode]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
        <span className="shrink-0 font-bold text-codialis-mint-clair">
          {promotion.tauxMoyen === null ? '—' : `${promotion.tauxMoyen} %`}
        </span>
      </div>
      {/* Taux moyen de complétion de ses apprenants (TF-458) ; vide quand aucun n'a de taux. */}
      <div className="mt-3">
        <BarreAvancement
          avancement={{ faits: 0, total: 0, taux: promotion.tauxMoyen }}
          libelle={`Progression moyenne de ${promotion.libelle}`}
        />
      </div>
      <p className="mt-2 text-xs text-codialis-gray">
        <span className="font-semibold text-codialis-navy">
          {pluriel(promotion.apprenants, 'apprenant en formation', 'apprenants en formation')}
        </span>
        {promotion.suivis > 0 && `, dont ${pluriel(promotion.suivis, 'suivi', 'suivis')} par vous`}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-codialis-gray">
        {promotion.periode ? (
          <span>
            Du {jourLisible(promotion.periode.debut)} au {jourLisible(promotion.periode.fin)}
          </span>
        ) : (
          <span>Apprentissage · entrées échelonnées</span>
        )}
        {promotion.aujourdhui && (
          <Pastille
            texte={`Aujourd’hui : ${promotion.aujourdhui.libelle.toLowerCase()}`}
            ton={promotion.aujourdhui.auCentre ? 'info' : 'neutral'}
          />
        )}
        {promotion.enRetard > 0 && <Pastille texte={`${promotion.enRetard} en retard`} ton="danger" />}
      </div>
    </>
  );
  if (!peutAcceder('instructor-promos')) {
    return <div className="min-w-0 rounded-xl border border-gray-100 p-4">{contenu}</div>;
  }
  return (
    <Link
      to={`/instructor-promos?promotion=${encodeURIComponent(promotion.cle)}`}
      className="block min-w-0 rounded-xl border border-gray-100 p-4 transition hover:bg-gray-50"
    >
      {contenu}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Mes apprenants
// ---------------------------------------------------------------------------

function DateApprenant({ libelle, valeur }: { libelle: string; valeur: DateCleLue | null }): JSX.Element {
  return (
    <div className="min-w-0 rounded-lg bg-codialis-sand px-2 py-2 sm:px-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-codialis-gray">{libelle}</div>
      <div className={`text-sm font-semibold ${valeur ? 'text-codialis-navy' : 'text-amber-700'}`}>
        {valeur ? jourLisible(valeur.date) : 'À fixer'}
      </div>
      {valeur?.provenance && <div className="text-[11px] leading-tight text-codialis-gray">{valeur.provenance}</div>}
    </div>
  );
}

function LigneApprenant({
  ligne,
  onConsulter,
}: {
  ligne: ApprenantDuFormateur;
  onConsulter: (consultation: Consultation) => void;
}): JSX.Element {
  return (
    <div className={`rounded-xl border p-4 ${ligne.nouveau ? 'border-codialis-mint/60 bg-codialis-mint/5' : 'border-gray-100'}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-display font-bold text-codialis-navy">{ligne.apprenant}</span>
            {ligne.nouveau && <Pastille texte="Nouveau" ton="mint" />}
          </div>
          <div className="text-xs text-codialis-gray">
            {[ligne.formation ?? 'Formation à préciser', `Antenne ${ligne.siteCode}`, ligne.mode]
              .join(' · ')}
            {ligne.referentDepuis && ` · référent depuis le ${jourLisible(ligne.referentDepuis)}`}
          </div>
        </div>
        <Pastille texte={ligne.etatLibelle} ton={ligne.etatTon} />
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        <DateApprenant libelle="Début" valeur={ligne.debut} />
        <DateApprenant libelle="Fin" valeur={ligne.fin} />
        <DateApprenant libelle="Examen" valeur={ligne.examen} />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {ligne.convocations.length === 0 && (
          <span className="text-xs text-codialis-gray">Aucune convocation reçue pour l’instant.</span>
        )}
        {ligne.convocations.map((convocation) => (
          <button
            key={convocation.documentId}
            type="button"
            onClick={() =>
              onConsulter({
                libelle: `${convocation.libelle} · ${ligne.apprenant}`,
                url: urlConvocationApprenant(ligne.apprenantId, convocation.documentId),
                nomFichier: `${convocation.type === 'CONVOCATION' ? 'convocation' : 'convocation-examen'}.pdf`,
              })
            }
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50"
          >
            <FileText className="h-3.5 w-3.5 text-codialis-sky" aria-hidden="true" />
            {convocation.libelle}
            <span className="font-normal text-codialis-gray">· {jourLisible(convocation.envoyeeLe.slice(0, 10))}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** Au-delà, la liste se replie : l'API la rend déjà triée, nouveaux et entrées proches d'abord. */
const APPRENANTS_VISIBLES = 6;

function ListeApprenants({
  apprenants,
  onConsulter,
}: {
  apprenants: ApprenantDuFormateur[];
  onConsulter: (consultation: Consultation) => void;
}): JSX.Element {
  const [deplie, setDeplie] = useState(false);
  const masques = apprenants.length - APPRENANTS_VISIBLES;
  const visibles = deplie ? apprenants : apprenants.slice(0, APPRENANTS_VISIBLES);
  return (
    <div className="space-y-3">
      {visibles.map((ligne) => (
        <LigneApprenant key={ligne.apprenantId} ligne={ligne} onConsulter={onConsulter} />
      ))}
      {masques > 0 && (
        <button
          type="button"
          onClick={() => setDeplie((valeur) => !valeur)}
          aria-expanded={deplie}
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-codialis-sky transition hover:bg-gray-50"
        >
          {deplie ? 'Réduire la liste' : `Afficher ${pluriel(masques, 'autre apprenant', 'autres apprenants')}`}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Colonne de droite
// ---------------------------------------------------------------------------

function CarteActionsEnAttente(): JSX.Element | null {
  const { peutAcceder } = useAuth();
  const { data } = useFichesATraiter(peutAcceder('form-fiches'));
  if (!peutAcceder('form-fiches') || !data) return null;
  const actions = data.rappel?.actions ?? [];
  return (
    <CarteSection
      titre="Fiches séquence"
      sousTitre={actions.length === 0 ? 'Aucune action en attente' : pluriel(actions.length, 'action en attente', 'actions en attente')}
      lien={{ to: '/form-fiches', libelle: 'Ouvrir' }}
      bordure={data.rappel?.bloque ? 'border-t-4 border-red-500' : undefined}
    >
      {data.rappel?.bloque && (
        <p className="mb-3 flex items-start gap-2 rounded-lg bg-red-50 p-3 text-xs text-red-700">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          Une demi-journée hors délai attend sa validation : vous ne pouvez soumettre aucune autre fiche.
        </p>
      )}
      <ul className="space-y-2">
        {actions.slice(0, 4).map((action) => (
          <li key={`${action.seanceId}-${action.etape}`} className="rounded-lg border border-gray-100 p-2.5">
            <div className="truncate text-sm font-semibold text-codialis-navy">{action.libelle}</div>
            <div className={`text-xs ${action.horsDelai ? 'font-semibold text-red-600' : 'text-codialis-gray'}`}>
              {action.etape}
            </div>
          </li>
        ))}
      </ul>
    </CarteSection>
  );
}

/** Profil salarié ou indépendant : ce que l'espace documentaire lui demande. */
function CarteProfil({ tableau }: { tableau: TableauDeBordFormateur }): JSX.Element {
  const { peutAcceder } = useAuth();
  const { data } = useMaFicheFormateur();
  const salarie = tableau.formateur?.statut === 'SALARIE';
  const fiche = data?.fiche ?? null;
  const piecesAFournir = fiche
    ? fiche.pieces.filter((piece) => piece.exigee && ['MANQUANTE', 'REFUSEE', 'PERIMEE'].includes(piece.etat))
    : [];
  const facturesACorriger = fiche ? fiche.factures.filter((facture) => facture.statut === 'A_CORRIGER').length : 0;

  return (
    <CarteSection
      titre={salarie ? 'Mes documents' : 'Mes documents & factures'}
      lien={peutAcceder('form-facturation') ? { to: '/form-facturation', libelle: 'Ouvrir' } : null}
    >
      <p className="text-sm text-codialis-gray">
        {salarie
          ? 'Formateur salarié : payé(e) par la paie, vous ne justifiez que votre qualification — ni facture, ni SIRET, ni convention.'
          : 'Formateur indépendant : vos pièces, votre convention et vos factures, vérifiées par le pôle avant transmission.'}
      </p>
      {fiche && (
        <ul className="mt-3 space-y-2 text-sm">
          <li className="flex items-center justify-between gap-2 rounded-lg bg-codialis-sand px-3 py-2">
            <span className="text-codialis-navy">Pièces à fournir</span>
            <Pastille texte={String(piecesAFournir.length)} ton={piecesAFournir.length > 0 ? 'warning' : 'success'} />
          </li>
          {!salarie && (
            <li className="flex items-center justify-between gap-2 rounded-lg bg-codialis-sand px-3 py-2">
              <span className="text-codialis-navy">Factures à corriger</span>
              <Pastille texte={String(facturesACorriger)} ton={facturesACorriger > 0 ? 'danger' : 'success'} />
            </li>
          )}
          {piecesAFournir.slice(0, 3).map((piece) => (
            <li key={piece.type} className="text-xs text-codialis-gray">
              · {piece.libelle} — {piece.etatLibelle.toLowerCase()}
            </li>
          ))}
        </ul>
      )}
    </CarteSection>
  );
}

function CarteNotifications(): JSX.Element {
  // Une ligne par envoi, quel que soit son canal, et le non lu de la cloche (TF-462).
  const { data } = useNotifications({});
  const recentes = (data?.notifications ?? []).slice(0, 4);
  const nonLues = data?.compteurs.nonLues ?? 0;
  return (
    <CarteSection
      titre="Dernières notifications"
      sousTitre={data ? pluriel(nonLues, 'non lue', 'non lues') : undefined}
      lien={{ to: '/notifications', libelle: 'Tout voir' }}
    >
      {recentes.length === 0 ? (
        <p className="text-sm text-codialis-gray">Aucune notification reçue.</p>
      ) : (
        <ul className="space-y-3">
          {recentes.map((notification) => (
            <li key={notification.id} className="flex items-start gap-2">
              <BellRing
                className={`mt-0.5 h-4 w-4 shrink-0 ${notification.lu ? 'text-codialis-gray' : 'text-codialis-sky'}`}
                aria-hidden="true"
              />
              <div className="min-w-0">
                <div className={`text-xs text-codialis-navy ${notification.lu ? '' : 'font-bold'}`}>
                  {notification.titre}
                </div>
                <div className="line-clamp-2 text-[11px] text-codialis-gray">{notification.corps}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </CarteSection>
  );
}

const ACTIONS_RAPIDES: { ecran: string; libelle: string; icone: LucideIcon; teinte: string }[] = [
  { ecran: 'emargement', libelle: 'Émargement', icone: PenLine, teinte: 'from-amber-500 to-orange-600' },
  { ecran: 'form-fiches', libelle: 'Fiches séquence', icone: ClipboardCheck, teinte: 'from-blue-500 to-indigo-600' },
  { ecran: 'form-elearning', libelle: 'Salle e-learning', icone: MonitorPlay, teinte: 'from-cyan-500 to-blue-600' },
  { ecran: 'form-fin', libelle: 'Fin de formation & ECF', icone: GraduationCap, teinte: 'from-rose-500 to-red-600' },
  { ecran: 'calendar', libelle: 'Mon planning', icone: CalendarDays, teinte: 'from-emerald-500 to-teal-600' },
  { ecran: 'messages', libelle: 'Messagerie', icone: MessageSquare, teinte: 'from-purple-500 to-fuchsia-600' },
];

function CarteActionsRapides(): JSX.Element {
  const navigate = useNavigate();
  const { peutAcceder } = useAuth();
  return (
    <section className="rounded-2xl bg-white p-4 shadow-card sm:p-6">
      <h2 className="font-display mb-4 font-bold text-codialis-navy">Actions rapides</h2>
      <div className="grid grid-cols-2 gap-2">
        {ACTIONS_RAPIDES.filter((action) => peutAcceder(action.ecran)).map(({ ecran, libelle, icone: IconeAction, teinte }) => (
          <button
            key={ecran}
            type="button"
            onClick={() => navigate(`/${ecran}`)}
            className="rounded-xl border border-gray-100 p-3 text-left transition hover:border-codialis-mint/40 hover:bg-gray-50"
          >
            <div className={`mb-2 flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br ${teinte} text-white`}>
              <IconeAction className="h-4 w-4" aria-hidden="true" />
            </div>
            <div className="text-xs font-semibold leading-tight text-codialis-navy">{libelle}</div>
          </button>
        ))}
      </div>
    </section>
  );
}

/** Fins de formation au second palier (trois mois par défaut) et ECF à saisir, pour les apprenants suivis. */
function CarteEcfEcheances(): JSX.Element | null {
  const { peutAcceder } = useAuth();
  const navigate = useNavigate();
  const { data } = useFinFormation('TOUS');
  if (!peutAcceder('form-fin') || !data) return null;

  const { compteurs } = data;
  const [, secondPalier] = data.paliers;
  const prochaine = data.apprenants.find((ligne) => ligne.palier === secondPalier && ligne.dateFin);
  return (
    <div className="rounded-2xl border-t-4 border-amber-500 bg-white p-4 sm:p-6 shadow-card">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display font-bold text-codialis-navy">Gestion ECF & Échéances</h2>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="text-sm font-bold text-amber-800">Fin dans {secondPalier} mois</div>
          <div className="mt-1 text-xs text-amber-600">
            {prochaine
              ? `Prochaine : ${prochaine.apprenant}, le ${jourLisible(prochaine.dateFin ?? '')}`
              : `Aucune fin de formation dans les ${secondPalier} mois`}
          </div>
          <div className="mt-2 text-2xl font-black text-amber-700">
            {compteurs.alerteSecondPalier} apprenant{compteurs.alerteSecondPalier > 1 ? 's' : ''}
          </div>
          <button
            type="button"
            onClick={() => navigate('/form-fin')}
            className="mt-3 min-h-[40px] w-full rounded-lg bg-amber-500 py-1.5 text-xs font-bold text-white transition hover:bg-amber-600"
          >
            Préparer les dossiers
          </button>
        </div>
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
          <div className="text-sm font-bold text-rose-800">ECF à saisir</div>
          <div className="mt-1 text-xs text-rose-600">
            {compteurs.ecfRealisees} réalisée{compteurs.ecfRealisees > 1 ? 's' : ''} sur{' '}
            {compteurs.ecfAttendues} attendue{compteurs.ecfAttendues > 1 ? 's' : ''}
          </div>
          <div className="mt-2 text-2xl font-black text-rose-700">{compteurs.ecfASaisir}</div>
          <button
            type="button"
            onClick={() => navigate('/form-fin')}
            className="mt-3 min-h-[40px] w-full rounded-lg bg-rose-500 py-1.5 text-xs font-bold text-white transition hover:bg-rose-600"
          >
            Saisir les résultats ECF
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

interface Consultation {
  libelle: string;
  url: string;
  nomFichier: string;
}

function phraseDuJour(tableau: TableauDeBordFormateur): string {
  const { compteurs } = tableau;
  const jour =
    compteurs.demiJourneesAujourdhui === 0
      ? 'Aucune demi-journée programmée aujourd’hui'
      : `Vous animez ${pluriel(compteurs.demiJourneesAujourdhui, 'demi-journée')} aujourd’hui`;
  const aCorriger = tableau.corrections.compteurs.devoirs + tableau.corrections.compteurs.quiz;
  const corrections =
    aCorriger === 0
      ? 'aucun travail à corriger'
      : `${pluriel(aCorriger, 'travail attend', 'travaux attendent')} votre correction`;
  const suivis =
    compteurs.apprenants === 0
      ? 'aucun apprenant ne vous est encore confié'
      : `${pluriel(compteurs.apprenants, 'apprenant suivi', 'apprenants suivis')}` +
        (compteurs.nouveaux > 0 ? `, dont ${pluriel(compteurs.nouveaux, 'nouveau', 'nouveaux')}` : '');
  return `${jour} · ${corrections} · ${suivis}.`;
}

export function DashboardFormateurPage(): JSX.Element {
  const { utilisateur, peutAcceder } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading, error } = useTableauDeBordFormateur();
  const [consultation, setConsultation] = useState<Consultation | null>(null);
  // Accès direct à la prochaine demi-journée à mener, sinon au parcours en salle.
  const prochaine = data?.sessionsDuJour.find((ligne) => ligne.cle === data.prochaineSession) ?? null;
  const actionProchaine = prochaine ? actionSession(prochaine) : null;

  const aujourdhui = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'Indian/Reunion',
  });

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <section
        className="relative overflow-hidden rounded-2xl p-4 text-white sm:p-6 lg:p-8"
        style={{ background: 'linear-gradient(135deg, #0B1F3A 0%, #7C2D12 100%)' }}
      >
        <div className="absolute -right-10 -top-10 h-64 w-64 rounded-full bg-amber-400/15 blur-3xl" />
        <div className="relative flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
          <div className="min-w-0">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-amber-400">
              Espace formateur · {aujourdhui}
            </div>
            <div className="mb-2 flex flex-wrap items-center gap-3">
              <h1 className="font-display text-3xl font-extrabold lg:text-4xl">Bonjour {utilisateur?.prenom}</h1>
              {data && (
                <span className="rounded-full border border-codialis-mint/30 bg-codialis-mint/20 px-2.5 py-1 text-xs font-bold text-codialis-mint">
                  {data.formateur?.statutLibelle ?? 'Fiche formateur à créer'}
                </span>
              )}
            </div>
            {data && <p className="max-w-xl text-white/80">{phraseDuJour(data)}</p>}
          </div>
          <div className="flex flex-wrap gap-3">
            {actionProchaine && prochaine && peutAcceder(actionProchaine.ecran) ? (
              <button
                type="button"
                onClick={() => navigate(actionProchaine.lien)}
                className="flex min-w-0 items-center gap-2 rounded-xl bg-amber-400 px-5 py-2.5 text-left text-sm font-semibold text-codialis-navy transition hover:bg-amber-300"
              >
                <PenLine className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="min-w-0">
                  {`${actionProchaine.libelle} · ${DEMI_JOURNEE[prochaine.demiJournee].toLowerCase()}`}
                </span>
              </button>
            ) : (
              peutAcceder('emargement') && (
                <button
                  type="button"
                  onClick={() => navigate('/emargement')}
                  className="flex items-center gap-2 rounded-xl bg-amber-400 px-5 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-amber-300"
                >
                  <PenLine className="h-4 w-4" aria-hidden="true" />
                  Ouvrir l’émargement
                </button>
              )
            )}
            {peutAcceder('form-fiches') && (
              <button
                type="button"
                onClick={() => navigate('/form-fiches')}
                className="flex items-center gap-2 rounded-xl border border-white/15 bg-white/10 px-5 py-2.5 text-sm font-medium text-white backdrop-blur transition hover:bg-white/20"
              >
                <ClipboardCheck className="h-4 w-4 text-amber-300" aria-hidden="true" />
                Fiches séquence
              </button>
            )}
          </div>
        </div>
      </section>

      {isLoading && <Chargement libelle="Chargement de votre tableau de bord…" />}
      {error && <MessageErreur message="Le tableau de bord n’a pas pu être chargé." />}

      {data && !data.formateur && (
        <p className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          Votre fiche formateur n’est pas encore créée : le pôle pédagogique la crée dans « Formateurs ». D’ici là,
          aucun apprenant ne peut vous être confié.
        </p>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
            <Indicateur
              icone={Users}
              teinte="bg-amber-100 text-amber-600"
              libelle="Apprenants suivis"
              valeur={data.compteurs.apprenants}
              detail="dont vous êtes le référent"
            />
            <Indicateur
              icone={UserPlus}
              teinte="bg-codialis-mint/10 text-codialis-mint"
              libelle="Nouveaux apprenants"
              valeur={data.compteurs.nouveaux}
              detail="confiés ces 30 derniers jours"
            />
            <Indicateur
              icone={CalendarDays}
              teinte="bg-red-100 text-red-600"
              libelle={`Entrées sous ${data.compteurs.preavisIntegrationJours} jours`}
              valeur={data.compteurs.entreesProchaines}
              detail="intégrations à préparer"
            />
            <Indicateur
              icone={FileCheck2}
              teinte="bg-codialis-sky/10 text-codialis-sky"
              libelle="À corriger"
              valeur={data.corrections.compteurs.devoirs + data.corrections.compteurs.quiz}
              detail={`${pluriel(data.corrections.compteurs.devoirs, 'devoir')} · ${pluriel(data.corrections.compteurs.quiz, 'quiz', 'quiz')}`}
            />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="min-w-0 space-y-6 lg:col-span-2">
              <CarteSection
                titre="Mes sessions du jour"
                sousTitre={[
                  data.sessionsDuJour.length === 0
                    ? 'Aucune demi-journée aujourd’hui'
                    : pluriel(data.sessionsDuJour.length, 'demi-journée'),
                  `${pluriel(data.compteurs.demiJourneesDuMois, 'feuille clôturée', 'feuilles clôturées')} ce mois`,
                ].join(' · ')}
                lien={peutAcceder('calendar') ? { to: '/calendar', libelle: 'Planning complet' } : null}
              >
                {data.sessionsDuJour.length === 0 ? (
                  <p className="text-sm text-codialis-gray">
                    Aucune demi-journée ne vous attend aujourd’hui : ni séance à votre nom, ni journée au centre
                    programmée pour vos sessions.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {data.sessionsDuJour.map((ligne) => (
                      <LigneSessionDuJour key={ligne.cle} ligne={ligne} prochaine={ligne.cle === data.prochaineSession} />
                    ))}
                  </div>
                )}
              </CarteSection>

              <CarteCorrections corrections={data.corrections} />

              <CarteSection
                titre="Mes promotions"
                sousTitre={
                  data.promotions.length === 0
                    ? 'Aucune promotion en formation'
                    : 'Avancement moyen et apprenants en retard'
                }
                lien={peutAcceder('instructor-promos') ? { to: '/instructor-promos', libelle: 'Détail' } : null}
              >
                {data.promotions.length === 0 ? (
                  <p className="text-sm text-codialis-gray">
                    Une promotion apparaît dès que vous y suivez un apprenant comme référent ou y animez une séance.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {data.promotions.map((promotion) => (
                      <CartePromotion key={promotion.cle} promotion={promotion} />
                    ))}
                  </div>
                )}
              </CarteSection>

              <CarteSection
                titre="Mes apprenants"
                sousTitre="Dates de début, de fin et d’examen, et convocations reçues."
              >
                {data.apprenants.length === 0 ? (
                  <p className="text-sm text-codialis-gray">
                    Aucun apprenant ne vous est confié pour l’instant. Le pôle pédagogique désigne le formateur
                    référent à l’intégration ; vous en êtes prévenu(e), avec les dates du dossier.
                  </p>
                ) : (
                  <ListeApprenants apprenants={data.apprenants} onConsulter={setConsultation} />
                )}
              </CarteSection>

              <CarteEcfEcheances />
            </div>

            {/* Sur tablette, la colonne de droite passe sous la principale en
                deux colonnes plutôt qu'en une file de cartes étirées. */}
            <div className="grid min-w-0 grid-cols-1 content-start gap-6 md:grid-cols-2 lg:grid-cols-1">
              <CarteActionsEnAttente />
              {data.formateur && <CarteProfil tableau={data} />}
              <CarteNotifications />
              <CarteActionsRapides />
            </div>
          </div>
        </>
      )}

      {consultation && (
        <VisionneusePiece
          ouverte
          libellePiece={consultation.libelle}
          urlBase={consultation.url}
          nomFichier={consultation.nomFichier}
          onFermer={() => setConsultation(null)}
        />
      )}
    </div>
  );
}
